import { useEffect, useState } from "react";
import { PageHeader } from "@/components/UIBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Simulator() {
  const [users, setUsers] = useState([]);
  const [pool, setPool] = useState([]);
  const [mappings, setMappings] = useState([]);

  // inbound state
  const [iUser, setIUser] = useState("");
  const [iStranger, setIStranger] = useState("+14150000123");
  const [iText, setIText] = useState("Hey, this is a new stranger.");

  // outbound state
  const [oMapping, setOMapping] = useState("");
  const [oText, setOText] = useState("Hello back!");

  const load = async () => {
    try {
      const [u, p, m] = await Promise.all([
        api.listUsers(),
        api.listPool(),
        api.listMappings(),
      ]);
      setUsers(u);
      setPool(p);
      setMappings(m.filter((x) => x.status === "active"));
      if (!iUser && u[0]) setIUser(u[0].proxy_number);
    } catch {/* ignore */}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const sendInbound = async () => {
    if (!iUser) return toast.error("Pick a user proxy");
    if (!iStranger || !iText) return toast.error("Stranger # and text required");
    try {
      const res = await api.simulate({
        from_number: iStranger,
        to_number: iUser,
        text: iText,
      });
      toast.success(
        res.new_mapping
          ? `New mapping created via ${res.shadow_number}`
          : res.status === "blocked"
          ? "Blocked (burn list)"
          : "Forwarded"
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const sendOutbound = async () => {
    const m = mappings.find((x) => x.id === oMapping);
    if (!m) return toast.error("Pick a mapping");
    try {
      const res = await api.simulate({
        from_number: m.user_real_number,
        to_number: m.shadow_number,
        text: oText,
      });
      if (res.status === "burned") {
        toast.success(`Burned ${res.stranger}`);
      } else {
        toast.success(`Delivered to ${res.stranger_number}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="simulator-page">
      <PageHeader
        title="Test Simulator"
        subtitle="// Inject mock Telnyx webhook payloads"
        testid="simulator-header"
      />

      <Tabs defaultValue="inbound" className="w-full">
        <TabsList className="bg-[#0e0e10] border border-[#1f1f23]">
          <TabsTrigger value="inbound" data-testid="tab-inbound" className="font-mono text-xs uppercase tracking-widest">
            Inbound (Stranger → User)
          </TabsTrigger>
          <TabsTrigger value="outbound" data-testid="tab-outbound" className="font-mono text-xs uppercase tracking-widest">
            Outbound (User → Shadow)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbound">
          <div className="border border-[#1f1f23] bg-[#0e0e10] p-6 mt-4 max-w-2xl">
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Target User (proxy_number)
                </Label>
                <Select value={iUser} onValueChange={setIUser}>
                  <SelectTrigger
                    data-testid="sim-inbound-user"
                    className="mt-1 bg-black border-[#27272a] font-mono"
                  >
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e10] border-[#27272a]">
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.proxy_number} className="font-mono">
                        {u.name} — {u.proxy_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Stranger Number (from)
                </Label>
                <Input
                  data-testid="sim-inbound-stranger"
                  value={iStranger}
                  onChange={(e) => setIStranger(e.target.value)}
                  className="mt-1 bg-black border-[#27272a] font-mono"
                />
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Text
                </Label>
                <Textarea
                  data-testid="sim-inbound-text"
                  value={iText}
                  onChange={(e) => setIText(e.target.value)}
                  className="mt-1 bg-black border-[#27272a] font-mono text-sm"
                />
              </div>

              <Button
                onClick={sendInbound}
                data-testid="sim-inbound-send"
                className="bg-white text-black hover:bg-zinc-200"
              >
                Inject Webhook
              </Button>
              <p className="font-mono text-[10px] text-zinc-600">
                {pool.length === 0
                  ? "⚠ Pool is empty — first inbound will fail. Add shadow numbers in Shadow Pool."
                  : `Pool: ${pool.length} numbers available`}
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="outbound">
          <div className="border border-[#1f1f23] bg-[#0e0e10] p-6 mt-4 max-w-2xl">
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Active Mapping (acts as "thread" the user is replying in)
                </Label>
                <Select value={oMapping} onValueChange={setOMapping}>
                  <SelectTrigger
                    data-testid="sim-outbound-mapping"
                    className="mt-1 bg-black border-[#27272a] font-mono"
                  >
                    <SelectValue placeholder="Select mapping" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e10] border-[#27272a]">
                    {mappings.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="font-mono text-xs">
                        {m.user_name} · {m.user_real_number} → {m.shadow_number} → {m.stranger_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Text (use BURN or RESET to kill mapping)
                </Label>
                <Textarea
                  data-testid="sim-outbound-text"
                  value={oText}
                  onChange={(e) => setOText(e.target.value)}
                  className="mt-1 bg-black border-[#27272a] font-mono text-sm"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={sendOutbound}
                  data-testid="sim-outbound-send"
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  Send Reply
                </Button>
                <Button
                  onClick={() => setOText("BURN")}
                  variant="outline"
                  data-testid="sim-outbound-burn"
                  className="bg-transparent border-red-900 text-red-400 hover:bg-red-950"
                >
                  use BURN
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
